import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ShopError, api, type GlossaryTerm } from '../api';
import { useMoney } from '../currency';
import { useT } from '../i18n';
import { usePageMeta, useJsonLd, absolute } from '../seo';

/**
 * Slovník pojmů – detail hesla.
 *
 * Vysvětlí pojem a prolinkuje na relevantní produkty a kategorie – to je jeho
 * hlavní SEO hodnota. `body` je prostý text: dělí se na odstavce, NIKDY se
 * nevkládá jako HTML (stejná zásada jako informační stránky).
 */
export function GlossaryTermPage() {
  const { slug = '' } = useParams();
  const { t } = useT();
  const { format } = useMoney();
  const [term, setTerm] = useState<GlossaryTerm | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .getGlossaryTerm(slug)
      .then((result) => {
        if (!alive) return;
        setTerm(result.term);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (!alive) return;
        setError(caught instanceof ShopError ? caught.message : 'Načtení se nezdařilo.');
        setTerm(null);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [slug]);

  usePageMeta(
    term === null
      ? null
      : {
          title: `${term.term} | ${t('glossary.title')} | Super Krmivo`,
          description: term.excerpt !== '' ? term.excerpt : term.body.replace(/\s+/g, ' ').slice(0, 160),
        },
  );

  // DefinedTerm + drobečky. Drobečky se vykreslují i jako HTML níž; JSON-LD
  // z nich udělá cestu ve výsledku vyhledávání.
  useJsonLd(
    term === null
      ? null
      : {
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'DefinedTerm',
              name: term.term,
              ...(term.excerpt === '' ? {} : { description: term.excerpt }),
              inDefinedTermSet: absolute('/slovnik'),
              url: absolute(`/slovnik/${term.slug}`),
            },
            {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Super Krmivo', item: absolute('/') },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: t('glossary.title'),
                  item: absolute('/slovnik'),
                },
                {
                  '@type': 'ListItem',
                  position: 3,
                  name: term.term,
                  item: absolute(`/slovnik/${term.slug}`),
                },
              ],
            },
          ],
        },
  );

  if (loading) return <p className="muted">{t('glossary.loading')}</p>;

  if (term === null) {
    return (
      <article className="glossary-term">
        <h1>{t('glossary.notFound')}</h1>
        <p className="muted">{error ?? t('glossary.notFoundMsg')}</p>
        <Link className="link" to="/slovnik">
          {t('glossary.back')}
        </Link>
      </article>
    );
  }

  const paragraphs = term.body.split(/\n{2,}/).filter((part) => part.trim() !== '');

  return (
    <article className="glossary-term">
      <nav className="breadcrumbs" aria-label="Drobečková navigace">
        <Link to="/slovnik">{t('glossary.title')}</Link>
        <span aria-hidden="true"> / </span>
        <span>{term.term}</span>
      </nav>

      <h1>{term.term}</h1>

      {term.aliases.length > 0 && (
        <p className="glossary-aliases muted">
          {t('glossary.aliases')}: {term.aliases.join(', ')}
        </p>
      )}

      {term.excerpt !== '' && <p className="glossary-excerpt lead">{term.excerpt}</p>}

      {paragraphs.map((paragraph, index) => (
        <p key={index}>{paragraph}</p>
      ))}

      {term.products.length > 0 && (
        <section className="glossary-related">
          <h2>{t('glossary.relatedProducts')}</h2>
          <div className="grid">
            {term.products.map((product) => (
              <div key={product.slug} className="card product-card">
                <Link to={`/produkt/${product.slug}`} className="favorite-link">
                  <div className="thumb" aria-hidden="true">
                    {product.imageUrl === null ? (
                      <span className="thumb-placeholder">{product.name.slice(0, 1)}</span>
                    ) : (
                      <img
                        src={product.imageUrl}
                        alt=""
                        loading="lazy"
                        decoding="async"
                      />
                    )}
                  </div>
                  <strong className="product-name">{product.name}</strong>
                  {product.priceFromMinor !== null && (
                    <span className="muted">
                      {t('glossary.priceFrom', { price: format(product.priceFromMinor) })}
                    </span>
                  )}
                </Link>
              </div>
            ))}
          </div>
        </section>
      )}

      {term.categories.length > 0 && (
        <section className="glossary-related">
          <h2>{t('glossary.relatedCategories')}</h2>
          <nav className="glossary-category-links">
            {term.categories.map((category) => (
              <Link
                key={category.slug}
                className="chip"
                to={`/?kategorie=${encodeURIComponent(category.slug)}`}
              >
                {category.name}
              </Link>
            ))}
          </nav>
        </section>
      )}

      <p className="glossary-foot">
        <Link className="link" to="/slovnik">
          ← {t('glossary.back')}
        </Link>
      </p>
    </article>
  );
}
